import { ReactNode } from 'react';
import classNames from 'classnames';
import noop from 'lodash/noop';

import { onEnterOrSpace } from 'wa-storybook/global/helpers';
import { Tag } from 'wa-storybook/components/tag/Tag';

import styles from './contact-boxout.module.scss';

type Props = {
  action: string | any;
  analyticsEvent?: () => void;
  label?: string;
  dataHook?: string;
  icon?: () => ReactNode;
  flip?: boolean;
  isDropdown?: boolean;
  ariaHasPopUp?: 'listbox' | boolean;
  ariaRole?: string;
  ariaLabelledBy?: string;
  id?: string;
  isHrefAction?: boolean;
};

type LinkProps = {
  href: string;
  target: string;
  rel: string;
  onClick: () => void;
  onKeyDown: () => void;
};

type FunctionProps = {
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
};

const getPropsForAction = (
  action: string | any,
  analyticsEvent?: () => void,
  isHrefAction?: boolean,
): LinkProps | FunctionProps | null => {
  const clickOrKeydownAction = () => {
    if (analyticsEvent) {
      analyticsEvent();
    }

    if (typeof action === 'function') {
      action();
    }
  };

  if (isHrefAction || typeof action === 'string') {
    return {
      href: isHrefAction ? action() : action,
      onClick: analyticsEvent || noop,
      onKeyDown: analyticsEvent ? onEnterOrSpace(analyticsEvent) : noop,
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }

  return {
    onClick: clickOrKeydownAction,
    onKeyDown: onEnterOrSpace(clickOrKeydownAction),
  };
};

const Item = ({
  action,
  analyticsEvent,
  label,
  icon,
  flip,
  dataHook,
  ariaRole,
  ariaHasPopUp,
  ariaLabelledBy,
  isDropdown,
  id,
  isHrefAction,
}: Props) => {
  const tagProps = getPropsForAction(action, analyticsEvent, isHrefAction);

  return (
    <Tag
      as={typeof action === 'string' || isHrefAction ? 'a' : 'button'}
      id={id}
      aria-haspopup={ariaHasPopUp}
      aria-labelledby={ariaLabelledBy}
      className={classNames(styles['list-item'], {
        [styles['list-dropdown']]: isDropdown,
      })}
      data-hook={dataHook}
      role={ariaRole}
      {...tagProps}
    >
      {label}
      {!!icon && (
        <div
          data-hook='icon'
          className={classNames(styles['icon'], flip && styles['icon--flip'])}
        >
          {icon()}
        </div>
      )}
    </Tag>
  );
};

Item.displayName = 'Item';

export default Item;
